/* eslint-disable jsx-a11y/iframe-has-title */
import React, { useEffect } from 'react';
import Modal from 'react-modal';

import { MODAL_CLASSNAME, MODAL_OVERLAY_CLASSNAME } from '../constants/classnames';
import {
  FAVOURITE_ADDED_EVENT,
  FAVOURITE_REMOVED_EVENT,
  FAVOURITES_FOLDER_NOT_SELECTED_EVENT,
  FAVOURITES_FOLDER_SELECTED_EVENT,
  FAVOURITES_FOLDER_SELECTOR_CLICKED_CLOSE_EVENT,
  FAVOURITES_FOLDER_SELECTOR_CLICKED_VIEW_FAVOURITES_EVENT,
  FAVOURITES_SUMMARY_FETCHED_EVENT,
} from '../constants/events';
import * as styles from '../styles';
import type { FavouritesSummary } from '../types/favourites';
import type { NextConfig } from '../types/nextConfig';

export type FavouritesProps = {
  isModalOpen: boolean;
  nextConfig: NextConfig;
  onCloseModal: VoidFunction;
  redirectToUserFavourites: VoidFunction;
  selectedProductCodeForModal: number | null;
  setFavouritesSummary: (s: FavouritesSummary) => void;
};

const modalMessageHandlers = (
  event: MessageEvent,
  handleClose: VoidFunction,
  redirectToUserFavourites: VoidFunction,
) => {
  switch (event.data) {
    case FAVOURITES_FOLDER_SELECTED_EVENT:
      document.dispatchEvent(new Event(FAVOURITE_ADDED_EVENT));
      break;
    case FAVOURITES_FOLDER_NOT_SELECTED_EVENT:
      document.dispatchEvent(new Event(FAVOURITE_REMOVED_EVENT));
      break;
    case FAVOURITES_FOLDER_SELECTOR_CLICKED_CLOSE_EVENT:
      handleClose();
      break;
    case FAVOURITES_FOLDER_SELECTOR_CLICKED_VIEW_FAVOURITES_EVENT:
      redirectToUserFavourites();
      break;
  }
};

export const Favourites = ({
  isModalOpen,
  nextConfig,
  onCloseModal,
  redirectToUserFavourites,
  selectedProductCodeForModal,
  setFavouritesSummary,
}: FavouritesProps) => {
  useEffect(() => {
    Modal.setAppElement('#NOTHSPageWrapper');

    const summaryListener = (event: CustomEvent<FavouritesSummary>) =>
      setFavouritesSummary(event.detail);
    document.addEventListener(FAVOURITES_SUMMARY_FETCHED_EVENT, summaryListener as EventListener);

    const modalListener = (e: MessageEvent) =>
      modalMessageHandlers(e, onCloseModal, redirectToUserFavourites);
    window.addEventListener('message', modalListener);

    /**
     * Trigger a re-sync to bypass race condition
     * Ideally we would add the Favourites event listeners earlier, e.g. _app.tsx to avoid the need for this,
     * but moving the event listeners into _app.tsx did not appear to resolve the issue.
     */
    document.dispatchEvent(new Event(FAVOURITE_ADDED_EVENT));

    return () => {
      document.removeEventListener(
        FAVOURITES_SUMMARY_FETCHED_EVENT,
        summaryListener as EventListener,
      );
      window.removeEventListener('message', modalListener);
    };
  }, [onCloseModal, redirectToUserFavourites, setFavouritesSummary]);

  return (
    <>
      <styles.ModalStyles />
      <Modal
        className={MODAL_CLASSNAME}
        contentLabel="Manage favourites folders"
        isOpen={isModalOpen}
        onRequestClose={onCloseModal}
        overlayClassName={MODAL_OVERLAY_CLASSNAME}
      >
        <iframe
          src={`${nextConfig.publicRuntimeConfig.hosts.search}/u/favourites/selector/${selectedProductCodeForModal}?source=productList`}
        />
      </Modal>
    </>
  );
};
